import _isEmpty from 'lodash/isEmpty'

import { SubscriptionInfo, SubscriptionItem, WebsiteDetail } from '@/hooks/use-glammatic-api'
import {
  isForceHasGlamDeskSubscriptionEnabled,
  isForceHasIntelligentWebsiteSubscriptionEnabled,
  isForceHasWebsiteSubscriptionEnabled,
} from '@/lib/html/env'

// Managed Website Packages
export const MANAGED_WEBSITE = 'Managed Website' // simple website only

// Other Packages
export const GLAM_DESK = 'GlamDesk' // desk only

// Intelligent Website Packages
export const INTELLIGENT_WEBSITE = 'Intelligent Website' // website only
export const GLAM_STARTER = 'GlamStarter' // website only
export const GLAM_GROWTH = 'GlamGrowth' // website + desk

// Social Packages
export const GLAM_PRO = 'GlamPro' // website + social + desk
export const GLAM_GROWTH_PLUS = 'GlamGrowth+' // social + desk
export const GLAM_ESSENTIALS = 'GlamEssentials' // social + website
export const GLAM_SOCIAL = 'GlamSocial' // social only
export const GLAM_SOCIAL_ESSENTIALS_WITH_INTELLIGENT_WEBSITE =
  'GlamSocial Essentials w/ Intelligent Website' // social + website

export function getSubscriptionNames(
  subscriptionData: SubscriptionItem[] | null | undefined = []
): string[] {
  if (!subscriptionData || _isEmpty(subscriptionData)) {
    return ['No Active Subscription']
  }

  const displayMap = {
    [GLAM_SOCIAL_ESSENTIALS_WITH_INTELLIGENT_WEBSITE]: GLAM_ESSENTIALS,
  }

  const names = subscriptionData
    .map(item => {
      const { product } = item.productPrice || {}
      const { name } = product || {}

      return displayMap[name as keyof typeof displayMap] || name || ''
    })
    .filter(Boolean)

  if (_isEmpty(names)) {
    return ['No Active Subscription']
  }

  return names
}

export function hasWebsiteSubscription(
  subscriptionData: SubscriptionItem[] | null | undefined = []
): boolean {
  const isForceHasWebsiteSubscription = isForceHasWebsiteSubscriptionEnabled()
  if (isForceHasWebsiteSubscription) {
    return true
  }

  if (_isEmpty(subscriptionData)) {
    return false
  }

  const hasIntelligentWebsite = hasIntelligentWebsiteSubscription(subscriptionData)
  if (hasIntelligentWebsite) {
    return true
  }

  const WHITELIST = ['website', MANAGED_WEBSITE]
  const names = getSubscriptionNames(subscriptionData)

  return names.some(name =>
    WHITELIST.some(whitelist => name.toLowerCase().includes(whitelist.toLowerCase()))
  )
}

export function hasIntelligentWebsiteSubscription(
  subscriptionData: SubscriptionItem[] | null | undefined = []
): boolean {
  const isForceHasIntelligentWebsiteSubscription =
    isForceHasIntelligentWebsiteSubscriptionEnabled()
  if (isForceHasIntelligentWebsiteSubscription) {
    return true
  }

  if (_isEmpty(subscriptionData)) {
    return false
  }

  const WHITELIST = ['intelligent', GLAM_STARTER, GLAM_ESSENTIALS, GLAM_PRO, GLAM_GROWTH]
  const names = getSubscriptionNames(subscriptionData)

  return names.some(name => {
    const lowerName = name.toLowerCase()

    return WHITELIST.some(whitelist => {
      if (whitelist === GLAM_GROWTH) {
        return lowerName === GLAM_GROWTH.toLowerCase()
      }

      return lowerName.includes(whitelist.toLowerCase())
    })
  })
}

export function hasSocialMediaSubscription(
  subscriptionData: SubscriptionItem[] | null | undefined = []
): boolean {
  const isForceHasWebsiteSubscription = isForceHasWebsiteSubscriptionEnabled()
  if (isForceHasWebsiteSubscription) {
    return true
  }

  if (_isEmpty(subscriptionData)) {
    return false
  }

  const WHITELIST = ['social', GLAM_PRO, GLAM_ESSENTIALS, GLAM_GROWTH_PLUS]

  const names = getSubscriptionNames(subscriptionData)

  return names.some(name =>
    WHITELIST.some(whitelist => name.toLowerCase().includes(whitelist.toLowerCase()))
  )
}

export function hasSocialMediaOnlySubscription(
  subscriptionData: SubscriptionItem[] | null | undefined = []
): boolean {
  return (
    hasSocialMediaSubscription(subscriptionData) && !hasWebsiteSubscription(subscriptionData)
  )
}

export function hasGlamDeskSubscription(
  subscriptionData: SubscriptionItem[] | null | undefined = []
): boolean {
  if (isForceHasGlamDeskSubscriptionEnabled()) {
    return true
  }

  if (_isEmpty(subscriptionData)) {
    return false
  }

  const WHITELIST = [GLAM_PRO, GLAM_GROWTH_PLUS, GLAM_GROWTH, GLAM_DESK]
  const names = getSubscriptionNames(subscriptionData)

  return names.some(name =>
    WHITELIST.some(whitelist => name.toLowerCase().includes(whitelist.toLowerCase()))
  )
}

export function getSubscriptionInfo(websiteDetail: WebsiteDetail): SubscriptionInfo {
  const { subscriptionData } = websiteDetail || {}
  const { subscriptionItems } = subscriptionData || {}

  const hasWebsite = hasWebsiteSubscription(subscriptionItems)
  const hasSocial = hasSocialMediaSubscription(subscriptionItems)
  const hasSocialOnly = hasSocialMediaOnlySubscription(subscriptionItems)
  const subscriptionNames = getSubscriptionNames(subscriptionItems)

  return {
    hasAnySubscription: hasWebsite || hasSocial,
    hasWebsite,
    hasSocial,
    hasSocialOnly,
    subscriptionNames,
  }
}
